import React, { useState, useEffect, useMemo } from 'react';
import { determineDisplayType, formatFieldName } from '../../utils/fieldEditorUtils';
import { useModal } from '../../context/ModalContext';

export default function EditorFieldConfig({
  selectedFieldIndex,
  pdfFields,
  configuration,
  onSaveField,
  onDeleteField,
  onCancelEdit
}) {
  const field = selectedFieldIndex !== null ? pdfFields[selectedFieldIndex] : null;
  const fieldConfig = field
    ? configuration?.fields?.find(f => field.configId && f.id === field.configId)
      || [...(configuration?.fields || [])].reverse().find(f => f.pdfFieldName === field.name && f.isVisible !== false)
    : null;

  const [formData, setFormData] = useState({
    pdfFieldName: '',
    displayType: 'text',
    groupName: '',
    displayLabel: '',
    minSize: '',
    maxSize: '',
    isVisible: true,
    displayOrder: 0,
    fontSize: '',
    fontFamily: '',
    fontColor: '#000000',
    textAlign: 'left',
    maxLength: '',
    bold: false,
    italic: false,
    underline: false,
    required: false,
    visibilityDependsOn: '',
    visibilityDependsOnValue: ''
  });

  // Load field data when selection changes
  useEffect(() => {
    if (field) {
      setFormData(prev => {
        // Preserve displayLabel when only pdfFieldName changes (after save)
        const shouldPreserveLabel = prev.pdfFieldName && prev.pdfFieldName !== field.name && prev.displayLabel;
        
        return {
          pdfFieldName: field.name || '',
          displayType: fieldConfig?.displayType || determineDisplayType(field.type || ''),
          groupName: fieldConfig?.groupName || field.groupName || '',
          displayLabel: shouldPreserveLabel ? prev.displayLabel : (fieldConfig?.displayLabel || ''),
          minSize: fieldConfig?.minSize || '',
          maxSize: fieldConfig?.maxSize || '',
          isVisible: fieldConfig?.isVisible !== false,
          displayOrder: fieldConfig?.displayOrder || 0,
          fontSize: fieldConfig?.fontSize ?? '',
          fontFamily: fieldConfig?.fontFamily ?? '',
          fontColor: fieldConfig?.fontColor || '#000000',
          textAlign: fieldConfig?.textAlign || 'left',
          maxLength: fieldConfig?.maxLength || '',
          bold: fieldConfig?.bold || false,
          italic: fieldConfig?.italic || false,
          underline: fieldConfig?.underline || false,
          required: fieldConfig?.required || false,
          visibilityDependsOn: fieldConfig?.visibilityDependsOn || '',
          visibilityDependsOnValue: fieldConfig?.visibilityDependsOnValue || ''
        };
      });
    }
  }, [field, fieldConfig, selectedFieldIndex]);

  if (selectedFieldIndex === null || !field) {
    return (
      <div className="no-selection" style={{ padding: '2rem 1rem', textAlign: 'center', color: '#999' }}>
        <p>Select a field on the PDF to edit its configuration.</p>
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
          Ctrl+Click for multi-select<br />
          Drag to move fields<br />
          Hold Ctrl while dragging to ignore alignment guides<br />
          Drag corners to resize
        </p>
      </div>
    );
  }

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      const savedConfig = await onSaveField(formData);
      if (savedConfig) {
        setFormData(prev => ({
          ...prev,
          fontSize: savedConfig.fontSize ?? '',
          fontFamily: savedConfig.fontFamily ?? '',
          fontColor: savedConfig.fontColor || '#000000',
          textAlign: savedConfig.textAlign || 'left',
          maxLength: savedConfig.maxLength ?? '',
          bold: !!savedConfig.bold,
          italic: !!savedConfig.italic,
          underline: !!savedConfig.underline,
          required: !!savedConfig.required,
          visibilityDependsOn: savedConfig.visibilityDependsOn || '',
          visibilityDependsOnValue: savedConfig.visibilityDependsOnValue || ''
        }));
      }
    } catch (error) {
      console.error('Failed to save field:', error);
    }
  };

  const { showConfirm } = useModal();

  const handleDelete = async () => {
    const ok = await showConfirm(`Delete field "${field.name}"?`, { title: 'Delete Field', danger: true });
    if (ok) {
      await onDeleteField(selectedFieldIndex);
    }
  };

  return (
    <div className="field-config-form">
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600 }}>Field Configuration</h3>

      {/* PDF Field Name */}
      <div className="form-group">
        <label>PDF Field Name</label>
        <input
          type="text"
          value={formData.pdfFieldName}
          onChange={(e) => handleChange('pdfFieldName', e.target.value)}
          className="form-control"
        />
      </div>

      {/* Display Type */}
      <div className="form-group">
        <label>Display Type</label>
        <select
          className="form-control"
          value={formData.displayType}
          onChange={(e) => handleChange('displayType', e.target.value)}
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="checkbox">Checkbox</option>
          <option value="radio">Radio</option>
          <option value="date">Date</option>
          <option value="signature">Signature</option>
          <option value="dropdown">Dropdown</option>
        </select>
      </div>

      {/* Group Name (for radio) */}
      {formData.displayType === 'radio' && (
        <div className="form-group">
          <label>Group Name</label>
          <input
            type="text"
            className="form-control"
            value={formData.groupName}
            onChange={(e) => handleChange('groupName', e.target.value)}
          />
        </div>
      )}

      {/* Display Label */}
      <div className="form-group">
        <label>Display Label</label>
        <input
          type="text"
          className="form-control"
          value={formData.displayLabel}
          onChange={(e) => handleChange('displayLabel', e.target.value)}
        />
      </div>

      {/* Font Size */}
      <div className="form-group">
        <label>Font Size</label>
        <input
          type="number"
          className="form-control"
          value={formData.fontSize}
          onChange={(e) => handleChange('fontSize', e.target.value)}
          min="6"
          max="72"
        />
      </div>

      {/* Font Family */}
      <div className="form-group">
        <label>Font Family</label>
        <select
          className="form-control"
          value={formData.fontFamily}
          onChange={(e) => handleChange('fontFamily', e.target.value)}
        >
          <option value="">Default</option>
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Georgia">Georgia</option>
          <option value="Verdana">Verdana</option>
          <option value="Helvetica">Helvetica</option>
          <option value="Tahoma">Tahoma</option>
          <option value="Trebuchet MS">Trebuchet MS</option>
          <option value="Comic Sans MS">Comic Sans MS</option>
          <option value="Raleway">Raleway</option>
        </select>
      </div>

      {/* Font Color */}
      <div className="form-group">
        <label>Font Color</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="color"
            value={formData.fontColor}
            onChange={(e) => handleChange('fontColor', e.target.value)}
            style={{ width: '40px', height: '30px', padding: '2px', cursor: 'pointer' }}
          />
          <input
            type="text"
            className="form-control"
            value={formData.fontColor}
            onChange={(e) => {
              if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                handleChange('fontColor', e.target.value);
              }
            }}
            style={{ flex: 1 }}
          />
        </div>
      </div>

      {/* Max Length */}
      <div className="form-group">
        <label>Max Length</label>
        <input
          type="number"
          className="form-control"
          value={formData.maxLength}
          onChange={(e) => handleChange('maxLength', e.target.value)}
          min="0"
        />
      </div>

      {/* Checkboxes */}
      {/* Text Alignment */}
      <div className="form-group">
        <label>Text Alignment</label>
        <select
          className="form-control"
          value={formData.textAlign}
          onChange={(e) => handleChange('textAlign', e.target.value)}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '0.75rem 0' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={formData.bold} onChange={(e) => handleChange('bold', e.target.checked)} />
          Bold
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={formData.italic} onChange={(e) => handleChange('italic', e.target.checked)} />
          Italic
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={formData.underline} onChange={(e) => handleChange('underline', e.target.checked)} />
          Underline
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={formData.required} onChange={(e) => handleChange('required', e.target.checked)} />
          Required
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={formData.isVisible} onChange={(e) => handleChange('isVisible', e.target.checked)} />
          Visible
        </label>
      </div>

      {/* Conditional Visibility */}
      <div className="form-group" style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
        <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem', display: 'block' }}>
          Conditional Visibility
        </label>
        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
          <label style={{ fontSize: '0.8rem' }}>Show only when</label>
          <select
            className="form-control"
            value={formData.visibilityDependsOn}
            onChange={(e) => {
              const newDependsOn = e.target.value;
              handleChange('visibilityDependsOn', newDependsOn);
              // Set a sensible default value for the controlling field type
              if (newDependsOn) {
                const isCheckbox = (configuration?.fields || []).some(
                  f => f.displayType === 'checkbox' && f.pdfFieldName === newDependsOn
                );
                // Checkbox: default to 'true' (Checked); Radio: reset to '' (user must pick)
                handleChange('visibilityDependsOnValue', isCheckbox ? 'true' : '');
              } else {
                handleChange('visibilityDependsOnValue', '');
              }
            }}
          >
            <option value="">Always visible</option>
            {/* Radio groups */}
            {(() => {
              const radioGroups = new Set();
              return (configuration?.fields || [])
                .filter(f => f.displayType === 'radio' && f.groupName)
                .reduce((opts, f) => {
                  if (!radioGroups.has(f.groupName)) {
                    radioGroups.add(f.groupName);
                    opts.push(
                      <option key={`radio-${f.groupName}`} value={f.groupName}>
                        Radio: {f.groupName}
                      </option>
                    );
                  }
                  return opts;
                }, []);
            })()}
            {/* Checkboxes */}
            {(configuration?.fields || [])
              .filter(f => f.displayType === 'checkbox' && f.pdfFieldName !== field?.name)
              .map(f => (
                <option key={`cb-${f.pdfFieldName}`} value={f.pdfFieldName}>
                  Checkbox: {f.displayLabel || f.pdfFieldName}
                </option>
              ))
            }
          </select>
        </div>

        {/* Radio group value selector */}
        {formData.visibilityDependsOn && (() => {
          const isRadioGroup = (configuration?.fields || []).some(
            f => f.displayType === 'radio' && f.groupName === formData.visibilityDependsOn
          );
          if (isRadioGroup) {
            const radioOptions = (configuration?.fields || []).filter(
              f => f.displayType === 'radio' && f.groupName === formData.visibilityDependsOn
            );
            return (
              <div className="form-group">
                <label style={{ fontSize: '0.8rem' }}>equals</label>
                <select
                  className="form-control"
                  value={formData.visibilityDependsOnValue}
                  onChange={(e) => handleChange('visibilityDependsOnValue', e.target.value)}
                >
                  <option value="">Select option...</option>
                  {radioOptions.map(opt => (
                    <option key={opt.pdfFieldName} value={opt.pdfFieldName}>
                      {opt.displayLabel || opt.pdfFieldName}
                    </option>
                  ))}
                </select>
              </div>
            );
          }
          // Checkbox value selector
          return (
            <div className="form-group">
              <label style={{ fontSize: '0.8rem' }}>equals</label>
              <select
                className="form-control"
                value={formData.visibilityDependsOnValue}
                onChange={(e) => handleChange('visibilityDependsOnValue', e.target.value)}
              >
                <option value="true">Checked</option>
                <option value="false">Unchecked</option>
              </select>
            </div>
          );
        })()}
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={handleSave} style={{ flex: 1 }}>Save Field</button>
        <button className="btn btn-secondary" onClick={onCancelEdit}>Cancel</button>
      </div>
      <button
        className="btn btn-danger"
        onClick={handleDelete}
        style={{ width: '100%', marginTop: '0.5rem' }}
      >
        Delete Field
      </button>
    </div>
  );
}
